/**
 * Known failures while reading a curriculum workbook or staging it.
 *
 * Add a future failure by inserting an entry in CATALOG before `unknown`.
 * Match the Postgres code, constraint name, or a stable part of the message.
 * The import dialog shows the title, what happened, why it stopped, and the resolution steps.
 */
export type CurriculumImportPhase = "upload" | "stage";

export interface CurriculumImportIssue {
  code: string;
  phase: CurriculumImportPhase;
  title: string;
  whatHappened: string;
  why: string;
  resolution: string[];
  sourceRow?: number;
  lesson?: string;
  savedCount?: number;
  expectedCount?: number;
  technicalDetail?: string;
}

export interface CurriculumImportFailureContext {
  phase: CurriculumImportPhase;
  message: string;
  sourceRow?: number;
  lesson?: string;
  fieldLabel?: string;
  savedCount?: number;
  expectedCount?: number;
  code?: string;
  details?: string;
  hint?: string;
  status?: number;
}

type DatabaseFailureSource = {
  message?: string;
  code?: string;
  details?: string | null;
  hint?: string | null;
  status?: number;
};

/** Keeps the Postgres code, details, and hint that `new Error(error.message)` would drop. */
export const databaseFailure = (error: DatabaseFailureSource | null | undefined): Error => {
  const failure = new Error(error?.message || "The request failed.");
  Object.assign(failure, {
    code: error?.code,
    details: error?.details || undefined,
    hint: error?.hint || undefined,
    status: error?.status,
  });
  return failure;
};

type CatalogEntry = {
  code: string;
  matches: (context: CurriculumImportFailureContext) => boolean;
  title: string;
  whatHappened: (context: CurriculumImportFailureContext) => string;
  why: (context: CurriculumImportFailureContext) => string;
  resolution: (context: CurriculumImportFailureContext) => string[];
};

const textOf = (error: unknown): Pick<CurriculumImportFailureContext, "message" | "code" | "details" | "hint" | "status" | "sourceRow"> => {
  if (error instanceof CurriculumImportError) return { message: error.issue.technicalDetail || error.issue.whatHappened, sourceRow: error.issue.sourceRow };
  if (error && typeof error === "object" && "message" in error) {
    const record = error as DatabaseFailureSource;
    const message = String(record.message || "The request failed.");
    const row = message.match(/\bRow (\d+)\b/);
    return { message, code: record.code, details: record.details || undefined, hint: record.hint || undefined, status: record.status, sourceRow: row ? Number(row[1]) : undefined };
  }
  if (typeof error === "string") {
    const row = error.match(/\bRow (\d+)\b/);
    return { message: error, sourceRow: row ? Number(row[1]) : undefined };
  }
  return { message: "The request failed." };
};

const includes = (context: CurriculumImportFailureContext, ...needles: string[]) => {
  const haystack = `${context.message} ${context.details || ""} ${context.hint || ""}`.toLowerCase();
  return needles.some((needle) => haystack.includes(needle.toLowerCase()));
};

const rowText = (context: CurriculumImportFailureContext) => context.sourceRow ? `Sheet row ${context.sourceRow}` : "This sheet";
const lessonText = (context: CurriculumImportFailureContext) => context.lesson ? ` in “${context.lesson}”` : "";
const progressText = (context: CurriculumImportFailureContext) =>
  context.savedCount != null && context.expectedCount != null
    ? ` ${context.savedCount} of ${context.expectedCount} rows are already saved in this import.`
    : "";

const CATALOG: CatalogEntry[] = [
  {
    code: "workbook.file_type",
    matches: (context) => context.code === "workbook.file_type" || includes(context, "choose an excel workbook"),
    title: "The file is not an Excel workbook",
    whatHappened: () => "The selected file was not read.",
    why: () => "Curriculum import reads .xlsx and .xls workbooks. Other files, including CSV, are not accepted here.",
    resolution: () => ["Save the sheet as an Excel workbook (.xlsx).", "Choose that workbook again."],
  },
  {
    code: "workbook.no_sheet",
    matches: (context) => context.code === "workbook.no_sheet" || includes(context, "does not contain a worksheet"),
    title: "The workbook has no worksheet",
    whatHappened: () => "The file opened, but there was no sheet to read.",
    why: () => "Import reads the first worksheet, or a sheet whose name is like “Grade 7”.",
    resolution: () => ["Add the curriculum table to the first worksheet.", "Choose the workbook again."],
  },
  {
    code: "workbook.missing_columns",
    matches: (context) => context.code === "workbook.missing_columns" || includes(context, "needs domain, unit, lesson name"),
    title: "Required columns are missing",
    whatHappened: () => "The worksheet header does not include every column the importer needs.",
    why: () => "The header row must contain Domain, Unit, Lesson Name, and Learning Outcomes. The match ignores capital letters.",
    resolution: () => [
      "Put those four headings on row 1 of the sheet.",
      "Keep one column per heading. A heading that says both “unit” and “lesson” is not used as the Unit column.",
      "Choose the workbook again.",
    ],
  },
  {
    code: "workbook.row_incomplete",
    matches: (context) => context.code === "workbook.row_incomplete" || includes(context, "needs a domain, unit, and lesson name"),
    title: "A row is missing its place in the outline",
    whatHappened: (context) => `${rowText(context)} does not have a domain, a unit, and a lesson name.`,
    why: () => "Every outcome row belongs to a lesson, and every lesson belongs to a unit and a domain. Blank cells are not copied down from the row above.",
    resolution: (context) => [
      `On ${rowText(context).toLowerCase()}, fill Domain, Unit, and Lesson Name.`,
      "Repeat those three values on each following outcome in the same lesson.",
      "Leave the other lesson columns blank on those following rows.",
      "Choose the workbook again.",
    ],
  },
  {
    code: "workbook.lesson_fields_repeated",
    matches: (context) => context.code === "workbook.lesson_fields_repeated" || includes(context, "repeats lesson details"),
    title: "Lesson details are repeated on an outcome row",
    whatHappened: (context) => `${rowText(context)}${lessonText(context)} repeats lesson details${context.fieldLabel ? ` in ${context.fieldLabel}` : ""}.`,
    why: () => "Standards, short standard, fundamental knowledge, applications, connections, vocabulary, notes, and the learning objective are stored once, on the lesson’s first row.",
    resolution: (context) => [
      `Clear the lesson columns on ${rowText(context).toLowerCase()}. Leave the learning outcome.`,
      "Keep Domain, Unit, and Lesson Name on that row so it stays with the lesson.",
      "Choose the workbook again.",
    ],
  },
  {
    code: "workbook.empty",
    matches: (context) => context.code === "workbook.empty" || includes(context, "no importable rows"),
    title: "The worksheet has no curriculum rows",
    whatHappened: () => "Nothing below the header could be imported.",
    why: () => "A row is imported when Domain, Unit, Lesson Name, or Learning Outcomes has text. Empty rows are skipped.",
    resolution: () => ["Add the curriculum under the header row.", "Choose the workbook again."],
  },
  {
    code: "workbook.unreadable",
    matches: (context) => context.phase === "upload" && (context.code === "workbook.unreadable" || includes(context, "unsupported file", "corrupted", "password", "bad zip", "could not find workbook")),
    title: "The workbook could not be read",
    whatHappened: () => "The file could not be opened as a workbook.",
    why: (context) => context.message || "The file may be damaged, password-protected, or not a real Excel workbook.",
    resolution: () => ["Open the file in Excel and save a new .xlsx copy.", "Remove any password from the workbook.", "Choose that copy."],
  },
  {
    code: "stage.subject_required",
    matches: (context) => context.code === "stage.subject_required" || includes(context, "select a subject"),
    title: "No subject is selected",
    whatHappened: () => "Staging did not start.",
    why: () => "Domains are stored on the subject selected in the curriculum filters.",
    resolution: () => ["Choose the subject in the filters above the outline.", "Stage the workbook again. Saved rows from an earlier attempt are kept."],
  },
  {
    code: "stage.signed_out",
    matches: (context) => context.code === "stage.signed_out" || includes(context, "sign in again"),
    title: "The admin session is missing",
    whatHappened: () => "Staging stopped because there is no signed-in user.",
    why: () => "Each staged row records who created it.",
    resolution: () => ["Sign in again as an admin.", "Open the import and continue staging. Rows already saved are skipped."],
  },
  {
    code: "stage.session_expired",
    matches: (context) => includes(context, "jwt expired", "invalid jwt", "pgrst301", "session expired"),
    title: "The sign-in expired while staging",
    whatHappened: (context) => `Staging stopped${context.sourceRow ? ` at ${rowText(context).toLowerCase()}` : ""}.${progressText(context)}`,
    why: () => "A long import can outlast the sign-in. Later rows are refused until you sign in again.",
    resolution: () => ["Sign in again as an admin.", "Continue staging. Rows that already have an objective are skipped."],
  },
  {
    code: "stage.permission",
    matches: (context) => context.code === "42501" || includes(context, "row-level security", "permission denied", "not authorized", "42501"),
    title: "This account cannot write the curriculum",
    whatHappened: (context) => `The database refused the write${context.sourceRow ? ` for ${rowText(context).toLowerCase()}` : ""}.${progressText(context)}`,
    why: () => "Only an admin profile can insert domains, units, lessons, objectives, and import rows.",
    resolution: () => ["Confirm you are signed in as an admin.", "Continue staging after the account is corrected. Saved rows are kept."],
  },
  {
    code: "stage.missing_table",
    matches: (context) => context.code === "42P01" || (includes(context, "does not exist") && /\brelation\b/i.test(`${context.message} ${context.details || ""}`) && !/\bcolumn\b/i.test(`${context.message} ${context.details || ""}`)),
    title: "A curriculum table is missing",
    whatHappened: (context) => `Staging could not save ${context.sourceRow ? rowText(context).toLowerCase() : "a row"} because a curriculum table is not in the database.${progressText(context)}`,
    why: () => "The page writes import batches, lessons, objectives, standards, and evidence. Those tables come from the curriculum migrations.",
    resolution: () => ["Apply the curriculum migrations.", "Reload this page.", "Continue staging. Saved rows are kept."],
  },
  {
    code: "stage.missing_column",
    matches: (context) => context.code === "42703" || context.code === "PGRST204" || includes(context, "does not exist", "schema cache", "could not find the"),
    title: "The database is missing a curriculum column",
    whatHappened: (context) => `Staging could not save ${context.sourceRow ? rowText(context).toLowerCase() : "a row"} because a column this import writes is not in the database.${progressText(context)}`,
    why: () => "Lesson text, the objective statement, and evidence fields come from a curriculum migration. The page is ahead of the database.",
    resolution: () => [
      "Apply the curriculum migrations, including the lesson, evidence, and shared-import changes.",
      "Reload this page.",
      "Continue staging. Saved rows are kept.",
    ],
  },
  {
    code: "stage.duplicate_objective_link",
    matches: (context) => includes(context, "curriculum_import_rows_objective_id_key", "key (objective_id)"),
    title: "This outcome is already attached to another import",
    whatHappened: (context) => `${rowText(context)}${lessonText(context)} uses an outcome that another import row already points to.${progressText(context)}`,
    why: () => "An outcome can be recorded on more than one grade only after the shared-import change. Until then, the second link is rejected.",
    resolution: (context) => [
      "Apply the migration that drops the single-import rule on curriculum import rows.",
      `Continue staging${context.sourceRow ? ` from ${rowText(context).toLowerCase()}` : ""}. Rows already linked are skipped.`,
    ],
  },
  {
    code: "stage.duplicate_source_row",
    matches: (context) => includes(context, "curriculum_import_rows_batch_id_source_row", "batch_id, source_row", "curriculum_import_rows_batch_id_source_row_key"),
    title: "This sheet row was already stored",
    whatHappened: (context) => `${rowText(context)} is already in this import, but it has no objective yet.${progressText(context)}`,
    why: () => "A sheet row can be stored once in an import. A previous attempt saved the row and stopped before linking its outcome.",
    resolution: () => ["Continue staging. The unfinished copy of that row is removed and written again."],
  },
  {
    code: "stage.duplicate_batch",
    matches: (context) => includes(context, "curriculum_import_batches_unique_automatic_import"),
    title: "This workbook already has an import",
    whatHappened: () => "A second import for the same file, subject, board, and grade was refused.",
    why: () => "One workbook is one import. Another attempt continues the existing batch instead of copying it.",
    resolution: () => ["Close this dialog and use the import already listed.", "Continue staging if its row count is still short of the sheet."],
  },
  {
    code: "stage.duplicate_name",
    matches: (context) => context.code === "23505" || includes(context, "duplicate key value", "23505"),
    title: "A curriculum name collided",
    whatHappened: (context) => `${rowText(context)}${lessonText(context)} tried to create a record whose name is already taken.${progressText(context)}`,
    why: () => "Domain names are unique in a subject, unit names in a domain, and lesson names in a unit. Two different rows that trim to the same name collide.",
    resolution: (context) => [
      `Compare ${rowText(context).toLowerCase()} with the earlier row that uses the same domain, unit, or lesson.`,
      "If they are the same lesson, repeat that exact name and put lesson details only on its first row.",
      "If they are different, give this one a distinct name.",
      "Choose the workbook again, or continue staging if the saved rows are still correct.",
    ],
  },
  {
    code: "stage.lesson_unit_mismatch",
    matches: (context) => includes(context, "must belong to a lesson in its selected unit"),
    title: "The outcome’s lesson is in a different unit",
    whatHappened: (context) => `${rowText(context)}${lessonText(context)} would attach an outcome to a lesson that does not belong to that unit.`,
    why: () => "An objective stores both a unit and a lesson, and the lesson’s unit must be the same.",
    resolution: (context) => [
      `Check the Unit and Lesson Name on ${rowText(context).toLowerCase()}.`,
      "Use the unit that already contains that lesson, or rename the lesson if it is new.",
      "Continue staging after the sheet is corrected.",
    ],
  },
  {
    code: "stage.not_null",
    matches: (context) => context.code === "23502" || includes(context, "not-null", "null value in column"),
    title: "A required value was empty",
    whatHappened: (context) => `${rowText(context)}${lessonText(context)} left a required database field empty.${progressText(context)}`,
    why: () => "Domain, unit, lesson, and the import row itself cannot be stored with a blank required field.",
    resolution: (context) => ["Read the technical detail to see which field was empty.", `Fill that field${context.sourceRow ? ` on ${rowText(context).toLowerCase()}` : ""} and continue staging.`],
  },
  {
    code: "stage.value_too_long",
    matches: (context) => context.code === "22001" || includes(context, "value too long"),
    title: "A cell is longer than the database allows",
    whatHappened: (context) => `${rowText(context)}${lessonText(context)} has text that does not fit the column.${progressText(context)}`,
    why: () => "A lesson or outcome field is stored in a column with a length limit.",
    resolution: (context) => [`Shorten the long cell on ${context.sourceRow ? rowText(context).toLowerCase() : "that row"}. The technical detail names the column.`, "Continue staging."],
  },
  {
    code: "stage.invalid_input",
    matches: (context) => context.code === "22P02" || context.code === "22003" || includes(context, "invalid input syntax"),
    title: "A value is in the wrong format",
    whatHappened: (context) => `${rowText(context)}${lessonText(context)} has a value the database could not store.${progressText(context)}`,
    why: () => "An identifier, number, or date was sent in a format that column does not accept.",
    resolution: (context) => ["Read the technical detail to see the rejected value.", "Correct the sheet or reload and continue staging if the selected subject is still valid."],
  },
  {
    code: "stage.required_field",
    matches: (context) => includes(context, "is required"),
    title: "A required value is blank",
    whatHappened: (context) => context.message || `${rowText(context)} is missing text the importer requires.`,
    why: () => "Domain, unit, lesson, and each learning outcome must contain text. Names made only of spaces are treated as blank.",
    resolution: (context) => [`Fill the blank value named in the technical detail${context.sourceRow ? `, on ${rowText(context).toLowerCase()}` : ""}.`, "Continue staging."],
  },
  {
    code: "stage.foreign_key",
    matches: (context) => context.code === "23503" || includes(context, "violates foreign key", "23503"),
    title: "A related curriculum record is missing",
    whatHappened: (context) => `${rowText(context)}${lessonText(context)} points at a subject, lesson, or objective that is not in the database.${progressText(context)}`,
    why: () => "The row was saved against a record that was removed, or the selected subject no longer exists.",
    resolution: () => ["Reload the page and confirm the subject filter.", "Continue staging so the domain, unit, and lesson are created again."],
  },
  {
    code: "stage.check_violation",
    matches: (context) => context.code === "23514" || includes(context, "violates check constraint", "23514"),
    title: "A value is not allowed",
    whatHappened: (context) => `${rowText(context)}${lessonText(context)} has a value the curriculum tables do not accept.${progressText(context)}`,
    why: () => "Status, evidence kind, and similar fields only allow a fixed set of values. A blank name is also rejected.",
    resolution: (context) => ["Read the technical detail to see which value was rejected.", `Correct ${context.sourceRow ? rowText(context).toLowerCase() : "that value"} and continue staging.`],
  },
  {
    code: "stage.not_single",
    matches: (context) => context.code === "PGRST116" || includes(context, "cannot coerce", "0 rows", "multiple (or no) rows"),
    title: "The database did not return the saved row",
    whatHappened: (context) => `The write for ${context.sourceRow ? rowText(context).toLowerCase() : "a row"} did not come back as one record.${progressText(context)}`,
    why: () => "The insert may have been blocked by access rules, or more than one existing record matched the name.",
    resolution: () => ["Confirm you are signed in as an admin.", "Look for a domain, unit, or lesson with the same name and keep one.", "Continue staging."],
  },
  {
    code: "stage.timeout",
    matches: (context) => context.code === "57014" || includes(context, "statement timeout", "57014", "timeout", "timed out"),
    title: "The database timed out on this row",
    whatHappened: (context) => `Saving ${context.sourceRow ? rowText(context).toLowerCase() : "a row"} took too long.${progressText(context)}`,
    why: () => "One row’s standards or lesson text can make a single request exceed the database time limit.",
    resolution: (context) => ["Continue staging. The import resumes at the first row that has no objective.", context.sourceRow ? `If it stops on ${rowText(context).toLowerCase()} again, shorten that lesson’s standards or notes and stage again.` : "If the same row stops again, shorten that lesson’s standards or notes."],
  },
  {
    code: "stage.payload",
    matches: (context) => context.status === 413 || includes(context, "payload too large", "request entity too large", "headers overflow", "413"),
    title: "The row is too large to send",
    whatHappened: (context) => `${rowText(context)}${lessonText(context)} could not be sent in one request.${progressText(context)}`,
    why: () => "Lesson standards, notes, or the learning objective text exceeded the request size.",
    resolution: (context) => [`Shorten the lesson text on ${context.sourceRow ? rowText(context).toLowerCase() : "that row"}.`, "Continue staging."],
  },
  {
    code: "stage.rate_limit",
    matches: (context) => context.status === 429 || includes(context, "too many requests", "429", "rate limit"),
    title: "Staging sent requests too quickly",
    whatHappened: (context) => `The service asked the import to wait.${progressText(context)}`,
    why: () => "Each outcome is saved on its own. A long sheet can briefly exceed the request limit.",
    resolution: () => ["Wait a minute, then continue staging.", "Saved rows are skipped."],
  },
  {
    code: "stage.network",
    matches: (context) => includes(context, "failed to fetch", "networkerror", "load failed", "network request failed", "econnreset", "enotfound"),
    title: "The connection dropped",
    whatHappened: (context) => `Staging lost the connection${context.sourceRow ? ` around ${rowText(context).toLowerCase()}` : ""}.${progressText(context)}`,
    why: () => "The browser could not reach the database. Rows saved before the drop stay in this import.",
    resolution: () => ["Check the network connection.", "Continue staging. It resumes at the first row without an objective."],
  },
  {
    code: "stage.version_required",
    matches: (context) => includes(context, "choose a draft curriculum version"),
    title: "Choose a draft version first",
    whatHappened: () => "Staging did not start because this page has no draft curriculum version to write into.",
    why: () => "A workbook is staged into the version open in the version menu. A published version cannot receive new rows.",
    resolution: () => ["Select a draft version, or reuse the published version to create one.", "Stage the workbook again."],
  },
  {
    code: "stage.published_version",
    matches: (context) => includes(context, "this curriculum version is published"),
    title: "This version is already published",
    whatHappened: () => "Staging stopped because the open version is published. None of its rows were changed.",
    why: () => "A published version keeps the tree students are already using. New workbook rows belong in a draft copy.",
    resolution: () => ["Use Reuse curriculum to copy this version into a new draft.", "Open that draft, then stage the workbook there."],
  },
  {
    code: "unknown",
    matches: () => true,
    title: "Staging stopped for a reason that is not in the catalog yet",
    whatHappened: (context) => context.message,
    why: () => "This failure does not match a known upload or staging error.",
    resolution: (context) => [
      "Read the technical detail and correct the sheet row or the account problem it names.",
      context.sourceRow ? `Continue staging after ${rowText(context).toLowerCase()} is fixed. Saved rows stay in this import.` : "Continue staging after the problem is fixed. Saved rows stay in this import.",
      "Add a catalog entry for this message in src/utils/curriculumImportIssues.ts so the next stop explains itself.",
    ],
  },
];

const buildIssue = (entry: CatalogEntry, context: CurriculumImportFailureContext): CurriculumImportIssue => ({
  code: entry.code,
  phase: context.phase,
  title: entry.code === "unknown" && context.phase === "upload" ? "The workbook stopped for a reason that is not in the catalog yet" : entry.title,
  whatHappened: entry.whatHappened(context),
  why: entry.why(context),
  resolution: entry.resolution(context),
  sourceRow: context.sourceRow,
  lesson: context.lesson,
  savedCount: context.savedCount,
  expectedCount: context.expectedCount,
  technicalDetail: context.message,
});

export class CurriculumImportError extends Error {
  readonly issue: CurriculumImportIssue;
  constructor(issue: CurriculumImportIssue) {
    super(issue.title);
    this.name = "CurriculumImportError";
    this.issue = issue;
  }
}

export const curriculumImportIssue = (code: string, context: Omit<CurriculumImportFailureContext, "code" | "message"> & { message?: string }): CurriculumImportIssue => {
  const entry = CATALOG.find((item) => item.code === code) ?? CATALOG[CATALOG.length - 1];
  return buildIssue(entry, { ...context, message: context.message || entry.title, code });
};

export const explainCurriculumImportFailure = (error: unknown, context: Partial<CurriculumImportFailureContext> & { phase: CurriculumImportPhase }): CurriculumImportIssue => {
  if (error instanceof CurriculumImportError) {
    return {
      ...error.issue,
      phase: context.phase || error.issue.phase,
      sourceRow: context.sourceRow ?? error.issue.sourceRow,
      lesson: context.lesson ?? error.issue.lesson,
      savedCount: context.savedCount ?? error.issue.savedCount,
      expectedCount: context.expectedCount ?? error.issue.expectedCount,
    };
  }
  const failure = textOf(error);
  const resolved: CurriculumImportFailureContext = {
    phase: context.phase,
    message: context.message || failure.message,
    sourceRow: context.sourceRow ?? failure.sourceRow,
    lesson: context.lesson,
    fieldLabel: context.fieldLabel,
    savedCount: context.savedCount,
    expectedCount: context.expectedCount,
    code: context.code || failure.code,
    details: context.details || failure.details,
    hint: context.hint || failure.hint,
    status: context.status || failure.status,
  };
  const entry = CATALOG.find((item) => item.matches(resolved)) ?? CATALOG[CATALOG.length - 1];
  return buildIssue(entry, resolved);
};
